import * as React from 'react';

import { ResaleMarker } from '@/components/atoms/ResaleMarker';
import { cn } from '@/components/atoms/primitives/utils';

const EVENTBRITE_SOURCE_RE = /eventbrite/i;

/**
 * WEB-1188 B10. Two phrases only: an Eventbrite-labelled feed names
 * Eventbrite; every other label (venue name, Ticketmaster, a demo feed)
 * reads as the venue's website. Straight apostrophe.
 */
export function listedFromSourcePhrase(sourceFeedLabel: string): string {
  if (EVENTBRITE_SOURCE_RE.test(sourceFeedLabel)) {
    return 'Listed from Eventbrite.';
  }
  return "Listed from the venue's website.";
}

export interface TrustFooterProps {
  /**
   * Human-readable name of the official ticketing or programming feed the
   * event was sourced from (e.g. "The Fillmore", "Eventbrite"). Selects
   * which of the two B10 phrases the primary source sentence uses.
   */
  sourceFeedLabel: string;
  /**
   * The resale verdict for a Ticketmaster resale (TicketsNow, `tmr`) listing
   * (`resaleVerdictOf`). Required so no mount can silently fall back to the
   * primary-listing copy.
   *
   * - `false` — primary listing: the three primary statements, teal voice.
   * - `true`  — confirmed resale (WEB-1098, DS 1.10.0 `.m-trust--resale`): the
   *   three resale statements, the teal withdrawn, the marker in the head row.
   * - `null`  — unknown (WEB-1101 fail-safe): the official-feed and
   *   never-resold-above-face claims are withheld, and nothing is asserted
   *   about resale either.
   */
  resaleListing: boolean | null;
  /** Additional CSS classes applied to the root container. */
  className?: string;
}

/**
 * TrustFooter — closes the event detail with a "How we list this" trust
 * block. Every clause is true for the listing it describes:
 *
 * - Primary: official-feed attribution, never-resold-above-face guarantee,
 *   unpaid-editorial-picks promise.
 * - Resale (DES-98 §3): it is a resale listing offered through Ticketmaster's
 *   resale marketplace, resellers set prices that can exceed face value, LOCI
 *   never inflates "low stock", and the editorial promise. No venue-feed name
 *   and no face value — neither is true or known for a resale row.
 *
 * Visual: the primary block wears the skyline wash and border (LOCI's
 * verified-feed voice). The resale block WITHDRAWS the teal — `--surface-alt`
 * ground, `--border-strong` hairline, `--fg-1` heading, marker right of the
 * heading. The withdrawal is the signal. Source:
 * `docs/design-system/1.10.0/reference/resale.html` §3.
 *
 * Pure presentational server component.
 */
export function TrustFooter({
  sourceFeedLabel,
  resaleListing,
  className,
}: TrustFooterProps): React.ReactElement {
  const isResale = resaleListing === true;
  const isPrimary = resaleListing === false;

  return (
    <div
      data-testid="trust-footer"
      data-variant={isResale ? 'resale' : isPrimary ? 'primary' : 'unknown'}
      className={cn(
        'border rounded-ds-md p-4 text-xs sm:text-sm text-fg-2 leading-[1.55]',
        isResale
          ? 'bg-ds-surface-alt border-border-strong'
          : 'bg-clt-skyline-wash border-clt-skyline',
        className,
      )}
    >
      <div data-testid="trust-footer-head" className="mb-1 flex items-center justify-between gap-3">
        <h3
          className={cn(
            'font-display text-small font-bold tracking-[0.02em]',
            isResale ? 'text-fg-1' : 'text-clt-skyline-glow-fg',
          )}
        >
          How we list this
        </h3>
        {isResale ? <ResaleMarker /> : null}
      </div>
      {isResale ? (
        <>
          <span data-testid="trust-footer-statement-resale-source">
            {
              "This is a resale listing: the tickets are offered by resellers through Ticketmaster's resale marketplace, not the venue's official feed."
            }
          </span>{' '}
          <span data-testid="trust-footer-statement-resale-price">
            {
              'Resellers set the prices, which can be above face value. LOCI never inflates "low stock" counts.'
            }
          </span>{' '}
        </>
      ) : null}
      {isPrimary ? (
        <>
          <span data-testid="trust-footer-statement-source">
            {listedFromSourcePhrase(sourceFeedLabel)}
          </span>{' '}
          <span data-testid="trust-footer-statement-resale">
            {
              'LOCI never shows resold tickets above face value, and never inflates "low stock" counts.'
            }
          </span>{' '}
        </>
      ) : null}
      <span data-testid="trust-footer-statement-editorial">
        {'Editorial picks aren’t paid — promoters can’t buy a ▲.'}
      </span>
    </div>
  );
}
